import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import logger from "@/lib/logger";

const AuthContext = createContext(null);

const TOKEN_KEY = "mordomo_token";

// Storage helpers tolerate environments where localStorage is unavailable
// (SSR, privacy mode, embedded webviews). Errors are silent in production.
const safeGetToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch (err) {
    logger.debug("[auth] localStorage.getItem unavailable:", err?.message);
    return null;
  }
};

const safeSetToken = (token) => {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch (err) {
    logger.debug("[auth] localStorage.setItem unavailable:", err?.message);
  }
};

const safeRemoveToken = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch (err) {
    logger.debug("[auth] localStorage.removeItem unavailable:", err?.message);
  }
};

export function AuthProvider({ children }) {
  // null = checking, false = unauthenticated, user object = authenticated
  const [user, setUser] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      // Skip the bootstrap call when there is no token; avoids a benign 401 in console.
      if (!safeGetToken()) {
        if (active) setUser(false);
        return;
      }
      try {
        const { data } = await api.get("/auth/me");
        if (active) setUser(data.user);
      } catch (err) {
        // Token invalid/expired — clear and treat as unauthenticated.
        logger.debug("[auth] bootstrap /me failed:", err?.response?.status);
        safeRemoveToken();
        if (active) setUser(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) safeSetToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.token) safeSetToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      logger.debug("[auth] logout endpoint error (non-fatal):", err?.message);
    }
    safeRemoveToken();
    setUser(false);
  }, []);

  const refreshUser = useCallback(async () => {
    const { data } = await api.get("/auth/me");
    setUser(data.user);
    return data.user;
  }, []);

  const value = useMemo(
    () => ({ user, setUser, login, register, logout, refreshUser }),
    [user, login, register, logout, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
